/**
 * WebSocket Service para escuchar eventos de QR desde el servidor
 * Se conecta automáticamente si el usuario tiene enable_parking activo
 * Escucha en: wss://empresa-service.intrare.app/ws/chat/{company_name}/
 */
import axios from 'axios'

class WebSocketService {
  constructor(store) {
    this.store = store;
    this.ws = null;
    this.baseUrl = process.env.VUE_APP_WEBSOCKET_URL || 'wss://empresa-service.intrare.app/ws/chat/';
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 3000;
    this.reconnectTimer = null;
    this.companyName = null;
  }

  /**
   * Conectar al WebSocket
   * @param {string} companyName - Nombre de la empresa (requerido)
   */
  connect(companyName) {
    if (!companyName) {
      console.error('Error: companyName es requerido para conectar WebSocket');
      return;
    }

    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.companyName = companyName;

    const processedCompanyName = companyName.replace(/[^a-zA-Z]/g, '').toLowerCase();
    const wsUrl = `${this.baseUrl}${processedCompanyName}/`;

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        this.reconnectAttempts = 0;
        this.store.commit('SET_WEBSOCKET_CONNECTED', true);
        console.log('WebSocket conectado a', wsUrl);
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          console.error('Error parseando mensaje WebSocket:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('Error WebSocket:', error);
        this.store.commit('SET_WEBSOCKET_CONNECTED', false);
      };

      this.ws.onclose = () => {
        console.log('WebSocket desconectado');
        this.store.commit('SET_WEBSOCKET_CONNECTED', false);
        if (!this._intentionalDisconnect) {
          this.attemptReconnect();
        }
      };
    } catch (error) {
      console.error('Error creando WebSocket:', error);
      this.attemptReconnect();
    }
  }

  /**
   * Intentar reconectar
   */
  attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;

      this.reconnectTimer = setTimeout(() => {
        this.connect(this.companyName);
      }, this.reconnectInterval);
    } else {
      console.error('Máximo de intentos de reconexión alcanzado');
    }
  }

  /**
   * Manejar mensaje recibido del WebSocket
   * Formato esperado: { "message": "ABC123XYZ" }
   */
  handleMessage(data) {
    // Verificar que haya un mensaje (QR)
    if (!data.message) {
      console.warn('[WebSocket] Mensaje inválido sin propiedad "message":', data);
      return;
    }

    const qrCode = data.message.trim();
    // Hacer fetch a la API para obtener la información del QR
    this.fetchQRData(qrCode);
  }

  /**
   * Obtener datos del QR desde la API
   */
  async fetchQRData(qrCode) {
    try {
      const url = `https://empresa-service.intrare.app/Parking/invitations/guard/qr/${qrCode}/`;
      
      const token = localStorage.getItem('user-token');
            
      if (!token) {
        throw new Error('No se encontró token de autenticación');
      }
      
      const response = await axios({
        method: 'get',
        url: url,
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + token
        }
      });

      // Guardar datos en el estado y mostrar modal
      this.store.commit('SET_WEBSOCKET_EVENT', {
        type: 'qr_scan',
        qr_code: qrCode,
        data: response.data
      });
      this.store.commit('SHOW_WEBSOCKET_MODAL', true);
    } catch (error) {
      console.error('Error obtiendo datos del QR:', error);
      
      // Mostrar error en modal
      const errorDetail = (error.response && error.response.data && error.response.data.detail) || 'Error al procesar el QR';
      this.store.commit('SET_WEBSOCKET_EVENT', {
        type: 'qr_error',
        qr_code: qrCode,
        error: errorDetail,
        message: error.message
      });
      this.store.commit('SHOW_WEBSOCKET_MODAL', true);
    }
  }

  /**
   * Desconectar
   */
  disconnect() {
    // Marcar como desconectado intencionalmente para evitar reconexiones
    this._intentionalDisconnect = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.onclose = null; // evitar que onclose dispare attemptReconnect
      this.ws.close();
      this.ws = null;
    }
    this.store.commit('SET_WEBSOCKET_CONNECTED', false);
  }

  /**
   * Verificar si está conectado
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

export default WebSocketService;
